// Alpine components for the tender search profile form.
//
// cpvPicker: combobox over the server-side CPV catalog (GET url?q=) that
// keeps a list of { code, label } chips.
// chipInput: free-text chips (keywords, exclude words, regions). Enter, comma
// or blur commits the current text; optional suggestions render a datalist.
export default function registerTenderSearch(Alpine) {
  registerTenderPreview(Alpine);
  registerTenderImport(Alpine);
  Alpine.data("cpvPicker", ({ selected = [], url }) => ({
    selected: selected.map((s) => ({ code: String(s.code), label: s.label || "" })),
    url,
    query: "",
    results: [],
    open: false,
    cursor: -1,
    _seq: 0,

    async search() {
      const q = (this.query || "").trim();
      if (q.length < 2) {
        this.results = [];
        this.open = false;
        return;
      }
      const seq = ++this._seq;
      try {
        const resp = await fetch(`${this.url}?q=${encodeURIComponent(q)}`, {
          headers: { Accept: "application/json" }
        });
        if (!resp.ok || seq !== this._seq) return;
        const data = await resp.json();
        const chosen = new Set(this.selected.map((s) => s.code));
        this.results = data.filter((r) => !chosen.has(String(r.code)));
        this.cursor = this.results.length ? 0 : -1;
        this.open = this.results.length > 0;
      } catch {
        this.results = [];
        this.open = false;
      }
    },

    pick(item) {
      if (!item) return;
      const code = String(item.code);
      if (!this.selected.some((s) => s.code === code)) {
        this.selected.push({ code, label: item.label || "" });
      }
      this.query = "";
      this.close();
    },

    pickFirst() {
      if (this.open && this.cursor >= 0) {
        this.pick(this.results[this.cursor]);
        return;
      }
      // Typing a bare 8-digit code and pressing Enter should also work.
      const raw = (this.query || "").trim();
      if (/^\d{8}$/.test(raw)) this.pick({ code: raw, label: "" });
    },

    move(delta) {
      if (!this.results.length) return;
      this.open = true;
      this.cursor = (this.cursor + delta + this.results.length) % this.results.length;
    },

    remove(code) {
      this.selected = this.selected.filter((s) => s.code !== code);
    },

    close() {
      this.open = false;
      this.cursor = -1;
      this.results = [];
    }
  }));

  Alpine.data("chipInput", ({ values = [], max = 20 }) => ({
    values: values.map((v) => String(v)),
    draft: "",
    max,

    commit() {
      const parts = (this.draft || "")
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);
      for (const part of parts) {
        if (this.values.length >= this.max) break;
        const exists = this.values.some((v) => v.toLowerCase() === part.toLowerCase());
        if (!exists) this.values.push(part);
      }
      this.draft = "";
    },

    onKeydown(event) {
      if (event.key === "Enter" || event.key === ",") {
        event.preventDefault();
        this.commit();
      } else if (event.key === "Backspace" && !this.draft && this.values.length) {
        this.values.pop();
      }
    },

    remove(index) {
      this.values.splice(index, 1);
    }
  }));
}

// Posts the whole profile form to the preview endpoint and swaps the returned
// HTML into the results container. Registered from registerTenderSearch.
export function registerTenderPreview(Alpine) {
  Alpine.data("tenderPreview", ({ url, autorun = false }) => ({
    url,
    autorun,
    loading: false,
    html: "",

    // After an import the server reloads the page with the window it searched;
    // re-running the search there puts the results back under the flash.
    //
    // Alpine initializes this component as soon as its start tag is parsed, so
    // the inputs that live inside and after it (the window, the profile id) are
    // not in the form yet — wait for the document before reading the form.
    init() {
      if (!this.autorun) return;

      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => this.run(), { once: true });
      } else {
        this.$nextTick(() => this.run());
      }
    },

    async run() {
      const form = this.$root.closest("form");
      if (!form) return;
      this.loading = true;
      try {
        const body = new FormData(form);
        // The edit form carries _method=patch; the preview endpoint is a plain POST.
        body.delete("_method");
        const token = document.querySelector('meta[name="csrf-token"]')?.content;
        const resp = await fetch(this.url, {
          method: "POST",
          body,
          headers: { Accept: "text/html", "X-CSRF-Token": token || "" }
        });
        this.html = await resp.text();
      } catch {
        this.html = "";
      } finally {
        this.loading = false;
      }
    }
  }));
}

// tenderImport: checkbox selection over the rendered preview results. The
// markup sits inside the profile form, so the selection is posted as a
// generated form rather than a nested one; the server redirects back to the
// profile with a flash.
export function registerTenderImport(Alpine) {
  Alpine.data("tenderImport", ({ ids = [] }) => ({
    ids: ids.map(String),
    selected: [],
    importing: false,

    allSelected() {
      return this.ids.length > 0 && this.selected.length === this.ids.length;
    },

    toggleAll(checked) {
      this.selected = checked ? [...this.ids] : [];
    },

    countLabel(template) {
      return template.replace("%{count}", String(this.selected.length));
    },

    submit(url) {
      if (this.selected.length === 0 || this.importing) return;
      this.importing = true;
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      const form = document.createElement("form");
      form.method = "post";
      form.action = url;
      form.style.display = "none";
      const field = (name, value) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.appendChild(input);
      };
      field("authenticity_token", token || "");
      this.selected.forEach((id) => field("external_ids[]", id));
      // Carry the searched window so the redirect reopens the form on it.
      ["preview_date_from", "preview_date_to"].forEach((name) => {
        const input = document.getElementById(name);
        if (input) field(name, input.value);
      });
      document.body.appendChild(form);
      form.submit();
    }
  }));
}
